// src/components/Modals/CreateFormModal.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Button,
  notification,
  Spin,
  AutoComplete,
  InputNumber,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { LuUpload, LuX } from "react-icons/lu";
import {
  useCreateFormMutation,
  useCreateFormTemplateMutation,
  useGetFormDetailsQuery,
  useUpdateFormTemplateMutation,
  useGetFormTemplatesQuery,
} from "@/store/api/apiSlice";
import dayjs from "dayjs";
import {
  useGetOrganizationsQuery,
  useUpdateFormMutation,
  useUploadFileMutation,
} from "../../store/api/apiSlice";
import {
  ALLOWED_LOGO_EXTENSIONS,
  ALLOWED_LOGO_MIME_TYPES,
} from "../../constants/organization";
import { getMimeFromExtension } from "../../utils/getMimeFromExtension";
import {
  getFormOrganizationDisplayName,
  getFormOrganizationLogoUrl,
  isDemoFormType,
  normalizeFormType,
  pickMediaUrl,
  resolveFormEditSource,
} from "../../utils/formTabs";
import { notifySuccessAfterModalClose } from "../../utils/notifications";
import { formatDate, getFormDetailsRecord } from "../../utils";

const CreateFormModal = ({
  visible,
  onClose,
  editData,
  istemplate = false,
  onSuccess,
  organizationId,
  refetch,
}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [selectedType, setSelectedType] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [titleSearchText, setTitleSearchText] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [logoFileList, setLogoFileList] = useState([]);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoImageLoaded, setLogoImageLoaded] = useState(false);
  const [logoImageError, setLogoImageError] = useState(false);

  const demoLogoTouchedRef = useRef(false);
  const lastEditIdRef = useRef(null);
  const skipEditRepopulateRef = useRef(false);
  const populatedEditIdRef = useRef(null);

  const syncDemoLogoPreview = useCallback(
    (url) => {
      const normalized = pickMediaUrl(url);

      if (!normalized) {
        setLogoUrl("");
        setLogoFileList([]);
        form.setFieldValue("demo_organization_logo", "");
        return;
      }

      setLogoUrl(normalized);
      setLogoFileList([
        {
          uid: "-1",
          name: "demo-logo",
          status: "done",
          url: normalized,
        },
      ]);
      form.setFieldValue("demo_organization_logo", normalized);
    },
    [form],
  );

  const previewLogoUrl =
    pickMediaUrl(logoFileList[0]?.url) || pickMediaUrl(logoUrl) || "";

  useEffect(() => {
    setLogoImageLoaded(false);
    setLogoImageError(false);
  }, [previewLogoUrl]);

  const showLogoPreview =
    Boolean(previewLogoUrl) && !logoUploading && !logoImageError;

  const { data: formTemplatesData, isLoading: templatesLoading } =
    useGetFormTemplatesQuery();

  const { data: organizationsData, isLoading: orgsLoading } =
    useGetOrganizationsQuery();

  const [createForm, { isLoading: isCreating }] = useCreateFormMutation();
  const [createFormTemplate, { isLoading: isCreatingTemplate }] =
    useCreateFormTemplateMutation();
  const [updateForm, { isLoading: isUpdating }] = useUpdateFormMutation();
  const [updateFormTemplate, { isLoading: isUpdatingTemplate }] =
    useUpdateFormTemplateMutation();
  const [uploadFile] = useUploadFileMutation();
  const { data: editFormDetails } = useGetFormDetailsQuery(editData?._id, {
    skip: !visible || !editData?._id,
  });

  const editSourceData = React.useMemo(() => {
    if (!editData) {
      return null;
    }

    return resolveFormEditSource(
      editData,
      getFormDetailsRecord(editFormDetails),
    );
  }, [editData, editFormDetails]);

  const isEditingDemo = isDemoFormType(editSourceData || editData);
  const formReady = !templatesLoading && (isEditingDemo || !orgsLoading);
  const isDemoForm =
    normalizeFormType(
      selectedType || editSourceData?.type || editData?.type,
    ) === "demo";
  const isPanelForm =
    normalizeFormType(
      selectedType || editSourceData?.type || editData?.type,
    ) === "panel";

  const organizations = useMemo(() => {
    if (!organizationsData?.data) return [];
    return organizationsData.data.map((org) => ({
      value: org._id,
      label: org.name,
      campaigns: org.campaigns || [],
    }));
  }, [organizationsData]);

  const formTemplateOptions = useMemo(() => {
    if (!formTemplatesData || !Array.isArray(formTemplatesData)) {
      return [];
    }

    return formTemplatesData.map((template) => ({
      value: template.title,
      label: (
        <div className="flex flex-col">
          <span className="font-medium">{template.title}</span>
          <span className="text-xs text-gray-500">
            {template.description?.substring(0, 50) || "No description"}
          </span>
        </div>
      ),
      template: template,
    }));
  }, [formTemplatesData]);

  const filteredTemplateOptions = titleSearchText
    ? formTemplateOptions.filter((option) =>
        option.value.toLowerCase().includes(titleSearchText.toLowerCase()),
      )
    : formTemplateOptions;

  useEffect(() => {
    if (!visible || !formReady) {
      return;
    }

    if (!editData) {
      form.resetFields();
      setSelectedType(null);
      setSelectedOrg(null);
      setAvailableCampaigns([]);
      setLogoUrl("");
      setLogoFileList([]);
    }
  }, [visible, editData, form, formReady]);

  useEffect(() => {
    if (!visible || !editData || !formReady) {
      return;
    }

    if (skipEditRepopulateRef.current) {
      return;
    }

    if (populatedEditIdRef.current === editData._id) {
      return;
    }

    populatedEditIdRef.current = editData._id;

    const sourceData =
      editSourceData ||
      resolveFormEditSource(editData, getFormDetailsRecord(editFormDetails));
    const isDemo = isDemoFormType(sourceData);

    if (!isDemo && organizations.length === 0) {
      return;
    }

    try {
      const formattedStartDate = formatDate(sourceData.start_date);
      const formattedEndDate = formatDate(sourceData.end_date);
      const resolvedDemoName = isDemo
        ? sourceData.demo_organization_name ||
          getFormOrganizationDisplayName(sourceData) ||
          form.getFieldValue("demo_organization_name") ||
          ""
        : undefined;
      const resolvedDemoLogo = isDemo
        ? getFormOrganizationLogoUrl(editData) ||
          getFormOrganizationLogoUrl(sourceData) ||
          logoUrl ||
          pickMediaUrl(form.getFieldValue("demo_organization_logo")) ||
          ""
        : "";

      form.setFieldsValue({
        title: sourceData.title || "",
        description: sourceData.description || "",
        type: sourceData.type || "",
        organization: isDemo
          ? undefined
          : sourceData.organization?._id || sourceData.organization || null,
        demo_organization_name: resolvedDemoName,
        campaign: isDemo
          ? undefined
          : sourceData.campaign?._id || sourceData.campaign || null,
        next: sourceData.next || "",
        anonymous: sourceData.anonymous || false,
        restart_form: sourceData.restart_form || false,
        show_next: true,
        show_previous: true,
        startDate: formattedStartDate ? dayjs(formattedStartDate) : undefined,
        endDate: formattedEndDate ? dayjs(formattedEndDate) : undefined,
        reward_amount: sourceData.reward_amount ?? undefined,
      });
      setSelectedType(sourceData.type);

      if (isDemo) {
        if (!demoLogoTouchedRef.current) {
          syncDemoLogoPreview(resolvedDemoLogo);
        }
      } else {
        syncDemoLogoPreview("");
      }

      setSelectedOrg(
        isDemo ? null : sourceData.organization?._id || sourceData.organization,
      );

      if (
        !isDemo &&
        (sourceData.organization?._id || sourceData.organization)
      ) {
        const orgId = sourceData.organization?._id || sourceData.organization;
        const selectedOrganization = organizations.find(
          (org) => org.value === orgId,
        );
        if (selectedOrganization) {
          setAvailableCampaigns(
            selectedOrganization.campaigns.map((campaign) => ({
              value: campaign._id,
              label: campaign.title || "Untitled Campaign",
            })),
          );
        }
      }
    } catch (error) {
      console.error("Error setting form values:", error);
      form.resetFields();
      setSelectedType(null);
      setSelectedOrg(null);
      setAvailableCampaigns([]);
    }
  }, [
    editData?._id,
    formReady,
    visible,
    form,
    organizations.length,
    syncDemoLogoPreview,
  ]);

  useEffect(() => {
    if (!visible) {
      setLogoUrl("");
      setLogoFileList([]);
      demoLogoTouchedRef.current = false;
      lastEditIdRef.current = null;
      skipEditRepopulateRef.current = false;
      populatedEditIdRef.current = null;
      return;
    }

    if (editData?._id && editData._id !== lastEditIdRef.current) {
      demoLogoTouchedRef.current = false;
      lastEditIdRef.current = editData._id;
    }

    if (editData && isDemoFormType(editData)) {
      setSelectedType(editData.type);
    }
  }, [visible, editData]);

  // Handle pre-populated organization data for new forms
  useEffect(() => {
    if (
      visible &&
      formReady &&
      organizationId &&
      !editData &&
      !isDemoForm &&
      organizations.length > 0
    ) {
      form.setFieldValue("organization", organizationId);
      setSelectedOrg(organizationId);

      // Set available campaigns from the selected organization
      const selectedOrg = organizations.find(
        (org) => org.value === organizationId,
      );
      if (selectedOrg) {
        setAvailableCampaigns(
          selectedOrg.campaigns?.map((campaign) => ({
            value: campaign._id,
            label: campaign.title || "Untitled Campaign",
          })) || [],
        );
      }
    }
  }, [
    visible,
    formReady,
    organizationId,
    editData,
    organizations,
    form,
    isDemoForm,
  ]);

  const validateLogoFile = (file) => {
    const inferredMime = file.type || getMimeFromExtension(file.name);
    const fileName = file.name?.toLowerCase() || "";
    const hasAllowedExtension = ALLOWED_LOGO_EXTENSIONS.some((ext) =>
      fileName.endsWith(ext),
    );
    const hasAllowedMime = ALLOWED_LOGO_MIME_TYPES.includes(inferredMime);

    if (!hasAllowedMime || !hasAllowedExtension) {
      notification.error({
        message: "Upload Error",
        description: "Only PNG, JPG/JPEG, and WEBP files are supported.",
      });
      return false;
    }

    if (file.size / 1024 / 1024 >= 2) {
      notification.error({
        message: "Upload Error",
        description: "Image must be smaller than 2MB.",
      });
      return false;
    }

    return true;
  };

  const handleLogoUpload = async (file) => {
    try {
      setLogoUploading(true);
      const inferredMime = file.type || getMimeFromExtension(file.name);
      const normalizedFile =
        !file.type && inferredMime
          ? new File([file], file.name, { type: inferredMime })
          : file;

      const data = await uploadFile({ file: normalizedFile }).unwrap();
      const uploadedUrl =
        pickMediaUrl(data?.file) ||
        pickMediaUrl(data?.data?.file) ||
        pickMediaUrl(data?.url) ||
        pickMediaUrl(data);

      if (!uploadedUrl) {
        throw new Error("Upload failed");
      }

      demoLogoTouchedRef.current = true;
      syncDemoLogoPreview(uploadedUrl);
      form.validateFields(["demo_organization_logo"]).catch(() => {});
      return uploadedUrl;
    } catch {
      notification.error({
        message: "Upload Failed",
        description: "Failed to upload logo. Please try again.",
      });
      return null;
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoInputChange = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!validateLogoFile(selectedFile)) {
      event.target.value = "";
      return;
    }

    await handleLogoUpload(selectedFile);
    event.target.value = "";
  };

  const handleRemoveLogo = () => {
    demoLogoTouchedRef.current = true;
    syncDemoLogoPreview("");
    form.validateFields(["demo_organization_logo"]).catch(() => {});
  };

  const handleLogoPreview = () => {
    if (!previewLogoUrl) return;

    const previewWindow = window.open("", "_blank");
    if (!previewWindow) return;

    const previewImage = previewWindow.document.createElement("img");
    previewImage.src = previewLogoUrl;
    previewImage.alt = "Organisation logo preview";
    previewImage.style.maxWidth = "80vw";
    previewImage.style.maxHeight = "80vh";
    previewImage.style.height = "auto";
    previewImage.style.display = "block";
    previewImage.style.borderRadius = "16px";

    previewWindow.document.body.style.margin = "0";
    previewWindow.document.body.style.backgroundColor = "#fff";
    previewWindow.document.body.style.display = "flex";
    previewWindow.document.body.style.justifyContent = "center";
    previewWindow.document.body.style.alignItems = "center";
    previewWindow.document.body.style.minHeight = "100vh";
    previewWindow.document.body.appendChild(previewImage);
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);

    if (normalizeFormType(value) === "demo") {
      form.setFieldsValue({
        organization: undefined,
        campaign: undefined,
        reward_amount: undefined,
      });
      setSelectedOrg(null);
      setAvailableCampaigns([]);
      return;
    }

    if (normalizeFormType(value) !== "panel") {
      form.setFieldsValue({ reward_amount: undefined });
    }

    setLogoUrl("");
    setLogoFileList([]);
    form.setFieldsValue({
      demo_organization_name: undefined,
      demo_organization_logo: "",
    });
  };

  const handleOrgChange = (value) => {
    setSelectedOrg(value);
    form.setFieldsValue({ campaign: undefined }); // Clear campaign selection

    // Update available campaigns based on selected organization
    const selectedOrganization = organizations.find(
      (org) => org.value === value,
    );

    if (selectedOrganization?.campaigns) {
      const campaignOptions = selectedOrganization.campaigns.map(
        (campaign) => ({
          value: campaign._id,
          label: campaign.title || "Untitled Campaign",
        }),
      );
      setAvailableCampaigns(campaignOptions);
    } else {
      setAvailableCampaigns([]);
    }
  };

  const handleTemplateSelect = (value, option) => {
    const template = option.template;
    setSelectedTemplate(template);

    // Populate form fields from template
    form.setFieldsValue({
      title: template.title,
      description: template.description,
      type: template.type,
      anonymous: template.anonymous,
      restart_form: template.restart_form || false,
    });

    setSelectedType(template.type);
  };

  const handleTitleChange = (text) => {
    setTitleSearchText(text);
  };

  const handleSubmit = async (values) => {
    try {
      const demoForm = normalizeFormType(values.type) === "demo";
      const panelForm = normalizeFormType(values.type) === "panel";
      const formValues = form.getFieldsValue(true);

      const payload = {
        title: values.title,
        description: values.description,
        type: values.type,
        start_date: values.startDate
          ? String(Math.floor(values.startDate.valueOf() / 1000))
          : null,
        end_date: values.endDate
          ? String(Math.floor(values.endDate.valueOf() / 1000))
          : null,
        anonymous: values.anonymous,
        restart_form: values.restart_form || false,
        show_next: true,
        show_previous: true,
        next: values.next || null,
        is_template: istemplate,
      };

      if (demoForm) {
        let demoName = String(
          formValues.demo_organization_name ??
            values.demo_organization_name ??
            "",
        ).trim();
        const demoLogo = demoLogoTouchedRef.current
          ? pickMediaUrl(logoUrl) || null
          : pickMediaUrl(logoUrl) ||
            pickMediaUrl(formValues.demo_organization_logo) ||
            pickMediaUrl(values.demo_organization_logo) ||
            null;

        payload.organization = null;
        payload.campaign = null;
        payload.demo_organization_name = demoName || null;
        payload.demo_organization_logo = demoLogo || "";
        if (demoLogo) {
          payload.image = demoLogo;
          payload.logo = demoLogo;
        } else if (editData?._id) {
          payload.image = "";
          payload.logo = "";
        }
      } else {
        payload.organization = values.organization;
        payload.campaign = values.campaign || null;
      }

      if (panelForm) {
        payload.reward_amount = values.reward_amount;
      } else if (editData?._id) {
        payload.reward_amount = null;
      }

      if (!editData?._id) {
        payload.status = "draft";
      }

      let successNotice = null;

      if (editData?._id) {
        if (istemplate || editData.is_template) {
          await updateFormTemplate({
            id: editData._id,
            data: payload,
          }).unwrap();
          successNotice = {
            message: "Template Updated",
            description: "Form template has been updated successfully.",
          };
        } else {
          await updateForm({
            id: editData._id,
            data: payload,
          }).unwrap();
          successNotice = {
            message: "Form Updated",
            description: "Form has been updated successfully.",
          };
        }
      } else {
        if (istemplate) {
          await createFormTemplate({
            data: payload,
          }).unwrap();
          successNotice = {
            message: "Template Created",
            description: "New template has been created successfully.",
          };
        } else {
          await createForm(payload).unwrap();
          successNotice = {
            message: "Form Created",
            description: "New form has been created successfully.",
          };
        }
      }

      skipEditRepopulateRef.current = true;

      if (onSuccess) {
        onSuccess();
      }

      onClose();

      if (successNotice) {
        notifySuccessAfterModalClose(successNotice);
      }

      if (refetch) {
        void refetch();
      }

      if (!editData?._id && !istemplate) {
        navigate(`/admin/forms`);
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          error.data?.message ||
          error.message ||
          "Failed to save form. Please try again.",
      });
    }
  };

  return (
    <Modal
      title={
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-xl font-semibold m-0">
            {editData ? "Edit Form" : "Create Form"}{" "}
            {istemplate && "Template"}{" "}
          </h3>
          <div
            className="bg-secondary h-8 w-8 flex justify-center items-center rounded-full cursor-pointer"
            onClick={onClose}
          >
            <LuX className="text-red-400" size={20} />
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      centered
      className="rounded-2xl [&_.ant-modal]:overflow-hidden [&_.ant-modal-content]:overflow-hidden"
      styles={{ body: { overflow: "hidden" } }}
      closable={false}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        preserve
        onFinish={handleSubmit}
        className="flex max-h-[calc(100vh-140px)] flex-col"
        initialValues={{
          show_next: true,
          show_previous: true,
          restart_form: false,
        }}
      >
        {!formReady ? (
          <div className="flex h-40 items-center justify-center">
            <Spin size="large" />
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              {/* Form Title with Template Suggestions */}
              <Form.Item
                name="title"
                label="Form Title"
                rules={[
                  {
                    required: true,
                    message: "Please enter form title",
                  },
                ]}
              >
                <AutoComplete
                  options={filteredTemplateOptions}
                  onSelect={handleTemplateSelect}
                  onChange={handleTitleChange}
                  placeholder="Type for suggestions or enter your own title"
                  className="h-11 rounded-xl"
                />
              </Form.Item>

              {/* Description */}
              <Form.Item name="description" label="Description">
                <Input.TextArea
                  placeholder="Type here..."
                  className="rounded-xl min-h-[120px]"
                />
              </Form.Item>

              <div
                className={`grid grid-cols-1 sm:grid-cols-2 ${
                  istemplate ? "lg:grid-cols-1" : "lg:grid-cols-3"
                } gap-4`}
              >
                {/* Form Type */}
                <Form.Item
                  name="type"
                  label="Form Type"
                  rules={[
                    {
                      required: true,
                      message: "Please select form type",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select--"
                    className="h-11 rounded-xl"
                    onChange={handleTypeChange}
                    options={[
                      {
                        value: "survey",
                        label: "Survey",
                      },
                      { value: "form", label: "Form" },
                      {
                        value: "complaint",
                        label: "Complaint",
                      },
                      { value: "enquiry", label: "Enquiry" },
                      { value: "demo", label: "Demo" },
                      { value: "panel", label: "Panel" },
                    ]}
                  />
                </Form.Item>

                {!istemplate && (
                  <Form.Item
                    name="startDate"
                    label="Start Date"
                    rules={[
                      {
                        required: true,
                        message: "Please select start date",
                      },
                    ]}
                  >
                    <DatePicker
                      className="h-11 w-full rounded-xl"
                      format="DD/MM/YYYY"
                      placeholder="Select start date"
                    />
                  </Form.Item>
                )}

                {!istemplate && (
                  <Form.Item
                    name="endDate"
                    label="End Date"
                    rules={[
                      {
                        required: true,
                        message: "Please select end date",
                      },
                    ]}
                  >
                    <DatePicker
                      className="h-11 w-full rounded-xl"
                      format="DD/MM/YYYY"
                      placeholder="Select end date"
                    />
                  </Form.Item>
                )}
              </div>

              {!istemplate && isDemoForm && (
                <div className="mb-4 mt-0 grid w-full grid-cols-1 gap-4 md:grid-cols-[180px_minmax(0,1fr)] md:items-start">
                  <Form.Item
                    name="demo_organization_logo"
                    label="Organisation logo"
                    required
                    className="mb-0 w-full max-w-[180px]"
                    rules={[
                      {
                        validator: () => {
                          const url =
                            pickMediaUrl(logoUrl) ||
                            pickMediaUrl(
                              form.getFieldValue("demo_organization_logo"),
                            );
                          if (!url) {
                            return Promise.reject(
                              new Error("Please upload the logo"),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <div className="relative h-full w-full rounded-lg border border-dashed border-[#d9d9d9]">
                      <label
                        htmlFor="demo-form-logo-input"
                        className={`flex h-full min-h-[112px] flex-col items-center justify-center gap-1.5 p-3 text-center ${
                          logoUploading ? "cursor-wait" : "cursor-pointer"
                        }`}
                      >
                        {logoUploading ? (
                          <>
                            <LoadingOutlined className="text-xl text-gray-600" />
                            <span className="text-xs text-gray-500">
                              Uploading...
                            </span>
                          </>
                        ) : showLogoPreview ? (
                          <>
                            {!logoImageLoaded && (
                              <LoadingOutlined className="text-xl text-gray-400" />
                            )}
                            <img
                              src={previewLogoUrl}
                              alt="Organisation logo preview"
                              className={`h-14 w-14 rounded-full object-cover ${
                                logoImageLoaded ? "block" : "hidden"
                              }`}
                              onLoad={() => {
                                setLogoImageLoaded(true);
                                setLogoImageError(false);
                              }}
                              onError={() => {
                                setLogoImageLoaded(false);
                                setLogoImageError(true);
                              }}
                            />
                            {logoImageLoaded && (
                              <div className="flex items-center justify-center gap-2">
                                <Button
                                  size="small"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleLogoPreview();
                                  }}
                                  className="h-7 rounded-lg bg-[#202835] px-3 text-xs text-white"
                                >
                                  Preview
                                </Button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleRemoveLogo();
                                  }}
                                  className="cursor-pointer border-0 bg-transparent p-0 text-xs text-red-500 underline"
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <LuUpload className="mx-auto" size={18} />
                            <span className="text-xs">Upload</span>
                          </>
                        )}
                      </label>
                      <input
                        id="demo-form-logo-input"
                        type="file"
                        accept={ALLOWED_LOGO_EXTENSIONS.join(",")}
                        className="sr-only"
                        tabIndex={-1}
                        disabled={logoUploading}
                        onChange={handleLogoInputChange}
                      />
                    </div>
                  </Form.Item>

                  <Form.Item
                    name="demo_organization_name"
                    label="Organisation Name"
                    required
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: "Please enter organisation name",
                      },
                    ]}
                    className="mb-0 min-w-0 w-full"
                  >
                    <Input
                      placeholder="Enter organisation name..."
                      className="h-11 rounded-xl"
                    />
                  </Form.Item>
                </div>
              )}

              {!istemplate && !isDemoForm && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <Form.Item
                    name="organization"
                    label="Select Organisation"
                    rules={[
                      {
                        required: true,
                        message: "Please select Organisation",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select--"
                      className="h-11 rounded-xl"
                      onChange={handleOrgChange}
                      options={organizations}
                      disabled={!!organizationId && !editData}
                    />
                  </Form.Item>

                  <Form.Item
                    name="campaign"
                    label="Select Campaign"
                    rules={[{ required: false }]}
                  >
                    <Select
                      allowClear
                      placeholder="Select--"
                      className="h-11 rounded-xl"
                      disabled={!selectedOrg}
                      options={availableCampaigns}
                    />
                  </Form.Item>
                </div>
              )}

              {!istemplate && isPanelForm && (
                <Form.Item
                  name="reward_amount"
                  label="Reward Amount"
                  rules={[
                    {
                      required: true,
                      message: "Please enter reward amount",
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    controls={false}
                    placeholder="Enter reward amount"
                    className="h-11 w-full rounded-xl"
                  />
                </Form.Item>
              )}

              {!istemplate && (
                <Form.Item
                  name="next"
                  label="Next Survey Link"
                  rules={[
                    {
                      validator: async (_, value) => {
                        if (!value) {
                          // If empty, it's valid
                          return Promise.resolve();
                        }
                        // Check URL format only if value exists
                        const urlPattern =
                          /^(https?:\/\/)?([a-zA-Z0-9.-]+)(:\d{2,5})?(\/.*)?$/;
                        if (!urlPattern.test(value)) {
                          return Promise.reject(
                            "Please enter a valid URL format",
                          );
                        }
                        // Add http:// if missing
                        if (!/^https?:\/\//i.test(value)) {
                          value = `http://${value}`;
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="Enter next survey link (optional)" />
                </Form.Item>
              )}

              {/* Make Users anonymous */}
              <div className="flex justify-between w-full">
                <div className="text-gray-700">Make Users Anonymous</div>
                <Form.Item
                  name="anonymous"
                  className="flex items-center justify-between w-full"
                  noStyle
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </div>
              <div className="flex justify-between w-full mt-2">
                <div className="text-gray-700">Restart the form</div>
                <Form.Item
                  name="restart_form"
                  className="flex items-center justify-between w-full"
                  noStyle
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </div>
            </div>

            <Button
              type="primary"
              htmlType="submit"
              className="mt-4 h-12 w-full shrink-0 rounded-xl bg-[#202835] text-white transition-colors hover:bg-[#202835]/90"
              loading={
                isCreating ||
                isCreatingTemplate ||
                isUpdating ||
                isUpdatingTemplate
              }
            >
              {editData
                ? istemplate || editData.is_template
                  ? "Update Template"
                  : "Update Form"
                : "Create Form"}
            </Button>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default CreateFormModal;
